import { useCallback, useMemo } from 'react';
import { useIsFetching, useQueries, useQuery } from '@tanstack/react-query';
import { HRM_PERMISSIONS } from '@/constant/hr/permissions';
import { useHasPermission } from '@/hooks/use-has-permission';
import { useAuthStore } from '@/state/store/auth';
import {
  BREAK_PERMISSIONS,
  getEligibilityRoles,
  getUserRolesInOrganization,
  resolveWorkStatusEligibility,
  WorkStatusEligibility,
} from '../services/work-status-eligibility.service';

const ELIGIBILITY_STALE_TIME = 5 * 60 * 1000;

/**
 * Whether lunch / overtime is on for the given employees (by IAM `UserId`), see
 * `work-status-eligibility.service.ts`. Returns a lookup that gives `undefined` while an
 * employee's roles are still loading (or IAM can't be read). Pass `enabled: false` to fetch nothing.
 */
const useWorkStatusEligibility = (userIds: readonly (string | undefined)[], enabled = true) => {
  const organizationId = useAuthStore((state) => state.selectedOrgId) ?? undefined;
  const ids = useMemo(
    () => [...new Set(userIds.filter((id): id is string => Boolean(id)))],
    [userIds]
  );

  // Plain queries on purpose: this only feeds a status read-out, so when IAM refuses the viewer
  // they just see a dash instead of an error toast.
  const { data: eligibilityRoles } = useQuery({
    queryKey: ['iam', 'work-status-eligibility-roles'],
    queryFn: getEligibilityRoles,
    enabled,
    staleTime: ELIGIBILITY_STALE_TIME,
    retry: false,
  });

  const rolesByUserId = useQueries({
    queries: ids.map((userId) => ({
      queryKey: ['iam', 'user-organization-roles', userId, organizationId],
      queryFn: () => getUserRolesInOrganization(userId, organizationId),
      enabled,
      staleTime: ELIGIBILITY_STALE_TIME,
      retry: false,
    })),
    combine: (results) =>
      new Map(ids.map((userId, index) => [userId, results[index]?.data] as const)),
  });

  return useCallback(
    (userId?: string): WorkStatusEligibility | undefined => {
      const roles = userId ? rolesByUserId.get(userId) : undefined;
      if (!eligibilityRoles || !roles) return undefined;
      return resolveWorkStatusEligibility(roles, eligibilityRoles);
    },
    [eligibilityRoles, rolesByUserId]
  );
};

/**
 * Whether lunch / overtime / breaks are on for one employee. The signed-in person's own comes
 * straight from their token; anybody else's is looked up in IAM (`undefined` while loading or when
 * IAM can't be read).
 */
/** Whether one employee's eligibility is still being read from IAM (false once it settles or fails). */
export const useIsWorkStatusEligibilityFetching = (userId: string | undefined) =>
  useIsFetching({ queryKey: ['iam', 'user-organization-roles', userId] }) +
    useIsFetching({ queryKey: ['iam', 'work-status-eligibility-roles'] }) >
  0;

export const useEmployeeWorkStatusEligibility = (
  userId: string | undefined
): WorkStatusEligibility | undefined => {
  const currentUserId = useAuthStore((state) => state.user?.itemId);
  const isSelf = Boolean(userId) && userId === currentUserId;
  const ownLunch = useHasPermission(HRM_PERMISSIONS.PROFILE.LUNCH_ENABLED);
  const ownOvertime = useHasPermission(HRM_PERMISSIONS.PROFILE.OVERTIME_ENABLED);
  const ownPermissions = useAuthStore((state) => state.user?.permissions) as string[] | undefined;
  const ownBreaks = BREAK_PERMISSIONS.some((permission) => ownPermissions?.includes(permission));
  const userIds = useMemo(() => [userId], [userId]);
  const eligibilityOf = useWorkStatusEligibility(userIds, Boolean(userId) && !isSelf);
  return isSelf
    ? { lunch: ownLunch, overtime: ownOvertime, breaks: ownBreaks }
    : eligibilityOf(userId);
};
